import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Modal, Col, Row, Button, ButtonGroup, Form, Alert, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLifeRing, faSearch, faExternalLinkAlt, faUserSlash, faLock, faHourglassHalf, faBook, faBalanceScale, faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { SAV_NOTICE_URL } from "./savNotice";
import swal from "sweetalert";
import Variables from "views/pages/Store/Sav/CreditPool/Variables";
import SavCustomerHistory from "views/pages/Store/Sav/Common/SavCustomerHistory";
import BaremeSuggestion from "views/pages/Store/Sav/Common/BaremeSuggestion";
import BaremeModal from "views/pages/Store/Sav/Common/BaremeModal";
import OrderSheetModal from "views/pages/Store/Sav/Common/OrderSheetModal";
import { buildSymfonyCustomersUrl } from "./customerDeepLink";
import { savOperations } from "state/ducks/store/sav";
import { extractTicketId } from "./freshdeskTicket";

// Un SAV porte sur une commande récente : au-delà, l'agent cherche dans le
// listing des commandes, pas dans un select.
const RECENT_ORDERS_LIMIT = 10;

/**
 * Titre d'étape : la fenêtre enchaîne quatre choses sans rapport visuel (ticket,
 * geste, motif interne, réponse au client). Les numéros donnent le fil.
 */
const Step = ({ num, title, hint }) => (
  <div className="sav-step">
    <span className="sav-step-num">{num}</span>
    <span className="sav-step-title">{title}</span>
    {hint && <span className="sav-step-hint">{hint}</span>}
  </div>
);

const MODE_STORAGE_KEY = "sav-modal-mode";

const EMPTY_FORM = {
  freeCreditAmount: 0,
  note: "",
  savCategory: "",
  vigilanceLevel: "",
  addCreditOrderAmount: false,
  creditOrderAmountPercentage: 100,
  productOptions: [],
};

/**
 * SAV créé depuis la fiche client, sans changer de page.
 *
 * Le SAV est rattaché au ticket Freshdesk d'origine quand il y en a un : c'est
 * alors Freshdesk qui répond au client (le mail habituel est coupé côté API pour
 * ne pas faire doublon), et le ticket est mis à jour tout seul.
 */
const PopupSav = (props) => {
  const { show, onHide, onEscapeKeyDown, customer, initialTicketId } = props;

  const dispatch = useDispatch();

  const propositions = useSelector((state) => state.store.sav.propositions, shallowEqual);
  // Ticket récupéré pour CETTE ouverture. Pas le store Redux : il y survivait
  // d'une ouverture à l'autre, et s'affichait sur la fiche d'un autre client.
  const [ticket, setTicket] = useState(null);

  // Les effets ci-dessous dépendent de valeurs scalaires, jamais de `customer`
  // ni de `propositions` : ces objets changent de référence à chaque mise à jour
  // du store — y compris celle que déclenche leur propre chargement — ce qui
  // relance l'effet en boucle et fige la page.
  const customerId = customer?.id;
  const hasProductCategories = (propositions.categories || []).length > 0;

  const [ticketIdInput, setTicketIdInput] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [responseText, setResponseText] = useState("");
  // Le message annonce le geste : s'il change après coup, le client lirait un
  // montant qui n'est pas celui qu'on lui crédite. On régénère quand le texte
  // sort de l'outil, on alerte quand l'agent l'a écrit lui-même.
  const [responseIsGenerated, setResponseIsGenerated] = useState(false);
  const [responseStale, setResponseStale] = useState(false);
  const [closeTicket, setCloseTicket] = useState(true);
  const [closeTicketTouched, setCloseTicketTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingTicket, setIsFetchingTicket] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderId, setOrderId] = useState("");
  // Propositions déjà en cours pour ce client : à voir AVANT d'en créer une,
  // sinon le client est dédommagé deux fois (le garde-fou ticket ne couvre que
  // le même ticket Freshdesk).
  const [inProgress, setInProgress] = useState([]);
  // Historique SAV du client (12 mois), barème et réponses types : trois demandes
  // de Tim du 28/09, pour ne plus répondre à l'aveugle.
  const [history, setHistory] = useState(null);
  const [baremeAnswers, setBaremeAnswers] = useState({});
  // Les produits et ingrédients à l'ORIGINE du SAV, tels que l'agent les a
  // choisis dans « Ce qui est concerné ». À ne pas confondre avec
  // `form.productOptions`, qui est ce qu'on OFFRE en compensation : jusqu'ici
  // seuls ces derniers étaient enregistrés, et la cause restait introuvable.
  const [concernedItems, setConcernedItems] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  // Qui signe. Vide : la réponse retombe sur le prénom SAV de l'établissement,
  // puis sur « L'équipe du Moulin ». Gardé d'un SAV à l'autre, c'est la même
  // personne qui les enchaîne.
  const [signature, setSignature] = useState(() => {
    try { return localStorage.getItem("savSignature") || ""; } catch (e) { return ""; }
  });
  // Contenu de la commande (produits et ingrédients avec leur prix) : le barème
  // fait choisir dedans au lieu de demander un montant à l'agent.
  const [orderItems, setOrderItems] = useState([]);
  const [orderSummary, setOrderSummary] = useState(null);
  const [showBareme, setShowBareme] = useState(false);
  const [showOrderSheet, setShowOrderSheet] = useState(false);
  // Deux façons de remplir la même fenêtre : le formulaire complet pour qui
  // enchaîne les SAV, le pas à pas pour qui en fait un de temps en temps.
  // Le choix est gardé d'une ouverture à l'autre.
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(MODE_STORAGE_KEY) === "guided" ? "guided" : "form"; } catch (e) { return "form"; }
  });
  const [stepIndex, setStepIndex] = useState(0);
  // Pas à pas : réponse à « ce SAV vient-il d'un ticket ? ». null tant que
  // l'agent n'a pas tranché — « Continuer » reste bloqué, pour qu'on ne passe
  // pas la première étape sans l'avoir lue (un ticket oublié = un doublon).
  const [fromTicket, setFromTicket] = useState(null);

  const changeMode = (value) => {
    setMode(value);
    setStepIndex(0);
    // Passer du formulaire au pas à pas avec un ticket déjà saisi : la question
    // est déjà répondue, inutile de la reposer.
    if (value === "guided" && extractTicketId(ticketIdInput) !== "") {
      setFromTicket(true);
    }
    try { localStorage.setItem(MODE_STORAGE_KEY, value); } catch (e) {}
  };

  // Les deux selects (catégorie SAV, vigilance) et les catégories produit
  // viennent du même endpoint : inutile de charger toute la page SAV ici.
  useEffect(() => {
    if (show && !hasProductCategories) {
      dispatch(savOperations.listPropositionCategories());
    }
  }, [show, dispatch, hasProductCategories]);

  // Réinitialise à chaque ouverture : une modale qui garde l'état du client
  // précédent est le meilleur moyen de créditer le mauvais client.
  useEffect(() => {
    if (show) {
      setTicket(null);
      setTicketIdInput(initialTicketId || "");
      setForm(EMPTY_FORM);
      setResponseText("");
      setResponseIsGenerated(false);
      setResponseStale(false);
      setCloseTicket(true);
      setCloseTicketTouched(false);
      setOrderId("");
      setOrders([]);
      setBaremeAnswers({});
      setConcernedItems([]);
      setSuggestion(null);
      setTemplateId("");
      setStepIndex(0);
      setFromTicket(initialTicketId ? true : null);
      setOrderItems([]);
      setOrderSummary(null);
    }
  }, [show, customerId, initialTicketId]);

  // Ouverte depuis un ticket (bloc « Partir d'un ticket ») : on le récupère tout
  // de suite, comme si l'agent avait cliqué « Récupérer ».
  useEffect(() => {
    if (show && customerId && initialTicketId) {
      fetchTicket(initialTicketId);
    }
    // fetchTicket n'est pas mémoïsée : on ne veut relancer que sur ces trois valeurs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, customerId, initialTicketId]);

  // Commandes du client, pour rattacher le SAV à l'une d'elles. C'est ce
  // rattachement qui donne l'établissement réel au lieu d'une déduction, et qui
  // rend utilisable l'option « % du montant de la commande ».
  //
  // Uniquement les dernières : un SAV porte sur une commande récente, et sans
  // limite l'API renvoie tout l'historique d'un habitué, paiements compris.
  // Une commande plus ancienne désignée par le ticket est ajoutée à la liste
  // au moment du « Récupérer ».
  useEffect(() => {
    if (!show || !customerId) return;
    dispatch(savOperations.listCustomerOrders(customerId, RECENT_ORDERS_LIMIT)).then(
      (res) => setOrders(res?.data?.result?.orders || []),
      () => setOrders([])
    );
  }, [show, customerId, dispatch]);

  useEffect(() => {
    if (!show || !customerId) {
      setInProgress([]);
      return;
    }
    dispatch(savOperations.listCustomerPropositions(customerId)).then(
      (res) => setInProgress(res?.data?.result?.items || []),
      () => setInProgress([])
    );
  }, [show, customerId, dispatch]);

  // Historique SAV du client, affiché en haut de la modale.
  useEffect(() => {
    if (!show || !customerId) {
      setHistory(null);
      return;
    }
    dispatch(savOperations.getCustomerSavHistory(customerId)).then(
      (res) => setHistory(res?.data?.result?.item || null),
      () => setHistory(null)
    );
  }, [show, customerId, dispatch]);

  // Contenu de la commande rattachée, pour le sélecteur de prix du barème.
  useEffect(() => {
    if (!show || !orderId) {
      setOrderItems([]);
      setOrderSummary(null);
      return;
    }
    dispatch(savOperations.listOrderItems(orderId)).then(
      (res) => {
        setOrderItems(res?.data?.result?.items || []);
        setOrderSummary(res?.data?.result?.order || null);
      },
      () => { setOrderItems([]); setOrderSummary(null); }
    );
  }, [show, orderId, dispatch]);

  // Proposition du barème : recalculée à chaque changement de catégorie, de
  // commande ou de réponse aux questions.
  useEffect(() => {
    if (!show || !form.savCategory) {
      setSuggestion(null);
      return;
    }
    dispatch(savOperations.getCompensationSuggestion({
      savCategory: form.savCategory,
      ...(orderId ? { orderId } : {}),
      answers: baremeAnswers,
    })).then(
      (res) => setSuggestion(res?.data?.result?.item || null),
      () => setSuggestion(null)
    );
  }, [show, form.savCategory, orderId, baremeAnswers, dispatch]);

  // Réponses types proposées pour la catégorie choisie.
  useEffect(() => {
    if (!show) {
      setTemplates([]);
      return;
    }
    dispatch(savOperations.listResponseTemplates(form.savCategory || null)).then(
      (res) => setTemplates(res?.data?.result?.items || []),
      () => setTemplates([])
    );
  }, [show, form.savCategory, dispatch]);

  // Changer de catégorie remet les réponses du barème à zéro : celles de la
  // catégorie précédente n'ont plus de sens.
  const handleFormChange = (name, value) => {
    if (name === 'savCategory') {
      setBaremeAnswers({});
      setConcernedItems([]);
      setTemplateId("");
    }
    handleChange(name, value);
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((prev) => ({
      ...prev,
      freeCreditAmount: suggestion.cagnotteAmount > 0
        ? (suggestion.cagnotteAmount / 100).toFixed(2)
        : prev.freeCreditAmount,
      productOptions: (suggestion.productOptions || []).map((o) => ({
        category: o.category,
        maxAmount: (o.maxAmount / 100).toFixed(2),
      })),
    }));
  };

  const cancelInProgress = async (proposition) => {
    const confirmed = await swal({
      title: "Annuler cette proposition ?",
      text: "Le client ne pourra plus faire son choix, et elle ne sera pas appliquée d'office à 48 h. "
        + "⚠️ La cagnotte déjà créditée reste créditée : ajustez-la à la main si besoin.",
      icon: "warning",
      buttons: { cancel: "Retour", confirm: "Annuler la proposition" },
    });
    if (confirmed !== true) return;
    try {
      await dispatch(savOperations.cancelProposition(proposition.id));
      setInProgress((prev) => prev.filter((p) => p.id !== proposition.id));
    } catch (e) {
      swal({ title: "Échec", text: "La proposition n'a pas été annulée.", icon: "error" });
    }
  };

  const formatDateTime = (iso) => (iso
    ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "");

  // Sans choix produit le client n'a rien à faire : le ticket peut être clos.
  // Avec choix, on attend sa réponse, donc on le laisse ouvert.
  useEffect(() => {
    if (!closeTicketTouched) {
      setCloseTicket(!form.productOptions || form.productOptions.length === 0);
    }
  }, [form.productOptions, closeTicketTouched]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const fetchTicket = async (idOverride) => {
    const ticketId = extractTicketId(typeof idOverride === "string" ? idOverride : ticketIdInput);
    if (!ticketId) {
      return swal({ title: "ID manquant", text: "Indiquez l'ID du ticket Freshdesk", icon: "warning" });
    }

    setIsFetchingTicket(true);
    try {
      // Le middleware API résout avec la réponse axios : { data: { code, result } }.
      const res = await dispatch(savOperations.getFreshdeskTicket(ticketId));
      const item = res?.data?.result?.item;
      setTicket(item || null);

      if (item?.existingSav) {
        swal({
          title: "Ce ticket a déjà un SAV",
          text: `${item.existingSav.label}. Vérifiez avant d'en créer un second : le client serait dédommagé deux fois.`,
          icon: "warning",
        });
      }

      setForm((prev) => ({
        ...prev,
        savCategory: item?.savCategory || prev.savCategory,
        note: prev.note || item?.suggestedReason || "",
      }));

      // Commande déjà renseignée sur le ticket : on la sélectionne, mais
      // seulement si elle appartient bien au client de la fiche ouverte.
      if (item?.order?.id) {
        if (String(item.order.customerId) === String(customer?.id)) {
          // Plus ancienne que les dernières chargées : on l'ajoute, sinon elle
          // serait sélectionnée sans apparaître dans la liste.
          setOrders((prev) => prev.some((o) => String(o.id) === String(item.order.id))
            ? prev
            : [{ id: item.order.id, numberOrder: item.order.numberOrder, date: item.order.date || '', price: item.order.price || 0 }, ...prev]);
          setOrderId(String(item.order.id));
        } else {
          swal({
            title: "Commande du ticket ignorée",
            text: `La commande ${item.order.numberOrder} indiquée sur le ticket appartient à un autre client. Choisissez la bonne commande dans la liste.`,
            icon: "warning",
          });
        }
      }
    } catch (e) {
      setTicket(null);
      swal({
        title: "Ticket introuvable",
        text: `Aucun ticket Freshdesk #${ticketId}`,
        icon: "error",
      });
    } finally {
      setIsFetchingTicket(false);
    }
  };

  const generateResponseText = async (forcedTemplateId) => {
    const chosen = forcedTemplateId !== undefined ? forcedTemplateId : templateId;
    const res = await dispatch(savOperations.previewResponse({
      customerId: customer?.id,
      cagnotteAmount: Math.round((parseFloat(String(form.freeCreditAmount).replace(",", ".")) || 0) * 100),
      productOptions: (form.productOptions || []).map((o) => ({
        category: o.category,
        maxAmount: Math.round((parseFloat(String(o.maxAmount).replace(",", ".")) || 0) * 100),
      })),
      ...(chosen ? { templateId: chosen } : {}),
      ...(orderId ? { orderId } : {}),
      savCategory: form.savCategory || null,
      concernedProducts: concernedItems,
      // Le remerciement d'ouverture n'a de sens que si le client a écrit.
      hasTicket: extractTicketId(ticketIdInput) !== "" ? 1 : 0,
      signature: signature.trim() || null,
    }));
    setResponseText(res?.data?.result?.item?.text || "");
    setResponseIsGenerated(true);
    setResponseStale(false);
  };

  // Signature du geste annoncé : tant qu'elle ne bouge pas, le message reste juste.
  const gestureSignature = JSON.stringify({
    amount: form.freeCreditAmount,
    percent: form.addCreditOrderAmount ? form.creditOrderAmountPercentage : null,
    options: (form.productOptions || []).map((o) => `${o.category}:${o.maxAmount}`),
    // L'excuse, l'équipe et la signature sortent de là : si l'un change, le
    // message écrit par l'outil n'est plus le bon.
    category: form.savCategory,
    concerned: (concernedItems || []).map((i) => i.label).join("|"),
    signature,
  });

  useEffect(() => {
    if (!show || responseText.trim() === "") return;

    // Message sorti de l'outil : on le refait, l'agent n'a rien à surveiller.
    if (responseIsGenerated) {
      const timer = setTimeout(() => { generateResponseText(); }, 600);
      return () => clearTimeout(timer);
    }
    // Message écrit à la main : on ne touche jamais aux mots de l'agent, on alerte.
    setResponseStale(true);
    return undefined;
    // generateResponseText n'est pas mémoïsée : la relancer sur ses propres
    // dépendances ferait une boucle. On ne réagit qu'au geste accordé.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gestureSignature]);

  const submit = async () => {
    if (!customer?.id) {
      return swal({ title: "Client manquant", text: "Sélectionnez d'abord un client", icon: "warning" });
    }
    if (!form.note) {
      return swal({ title: "Motif requis", text: "Indiquez le motif du SAV", icon: "warning" });
    }
    if (!form.savCategory) {
      return swal({ title: "Catégorie requise", text: "Indiquez la catégorie du SAV", icon: "warning" });
    }
    // Le pourcentage de la commande compte comme un contenu : une proposition
    // faite uniquement de « X % du montant de la commande » est valide.
    const hasPercent = form.addCreditOrderAmount && orderId && (form.creditOrderAmountPercentage || 0) > 0;
    if (form.freeCreditAmount <= 0 && !hasPercent && (!form.productOptions || form.productOptions.length === 0)) {
      return swal({ title: "Proposition vide", text: "Ajoutez un montant cagnotte, un pourcentage de la commande ou une option produit", icon: "warning" });
    }

    const ticketId = extractTicketId(ticketIdInput);

    // Le doute d'identité est rappelé ici : la bannière du haut peut avoir été
    // survolée, et c'est le dernier moment avant de créditer le mauvais client.
    if (identityWarning) {
      const stillOk = await swal({
        title: identityWarning.level === "danger" ? "Le ticket est à un autre client" : "Email du ticket inconnu",
        text: `${identityWarning.text}\n\nCréer quand même le SAV sur ${customer.firstname} ${customer.lastname} ?`,
        icon: "warning",
        buttons: { cancel: "Annuler", confirm: "Continuer" },
      });
      if (stillOk !== true) return;
    }

    const recap = [
      ticketId
        ? `Ticket Freshdesk #${ticketId} : mis à jour${responseText.trim() ? " et votre message envoyé au client" : ""}.`
        : `Un ticket Freshdesk sera créé${responseText.trim() ? ", et votre message y sera envoyé au client" : ""}.`,
      responseText.trim() ? null : "Pas de message écrit : le client recevra le mail automatique du Moulin.",
      ticketId && ticket?.isDone && responseText ? `⚠️ Le ticket était ${ticket.statusLabel.toLowerCase()} : la réponse va le rouvrir.` : null,
      "",
      orderId ? `Commande : ${(orders.find((o) => String(o.id) === String(orderId)) || {}).numberOrder || orderId}` : "Aucune commande rattachée.",
      form.freeCreditAmount > 0 ? `Cagnotte : ${Number(form.freeCreditAmount).toFixed(2)} €` : null,
      hasPercent ? `Cagnotte : ${form.creditOrderAmountPercentage} % du montant de la commande` : null,
      (form.productOptions || []).length ? `Choix proposé : ${(form.productOptions || []).map((o) => ((propositions.categories || []).find((c) => c.key === o.category) || {}).label || o.category).join(", ")}` : null,
      closeTicket ? (ticketId ? "Le ticket sera clôturé." : "Le ticket créé sera clôturé.") : null,
    ].filter((l) => l !== null).join("\n");

    const confirm = await swal({
      title: "Créer le SAV ?",
      text: recap,
      icon: "warning",
      buttons: { cancel: "Annuler", confirm: "Valider" },
    });
    if (confirm !== true) return;

    setIsSubmitting(true);
    try {
      const res = await dispatch(savOperations.createProposition({
        customerId: customer.id,
        orderId: orderId || null,
        reason: form.note,
        savCategory: form.savCategory,
        vigilanceLevel: form.vigilanceLevel || null,
        cagnotteFixedAmount: form.freeCreditAmount > 0 ? Math.round(form.freeCreditAmount * 100) : null,
        cagnottePercent: form.addCreditOrderAmount && orderId ? (form.creditOrderAmountPercentage || 0) : null,
        productOptions: (form.productOptions || []).map((o) => ({
          category: o.category,
          maxAmount: Math.round(parseFloat(o.maxAmount || 0) * 100),
        })),
        // Ce qui a CAUSÉ le SAV, à ne pas confondre avec productOptions ci-dessus
        // qui est ce qu'on offre.
        concernedProducts: concernedItems,
        freshdeskTicketId: ticketId || null,
        responseText: responseText.trim() !== "" ? responseText : null,
        responseTemplateId: templateId || null,
        closeTicket,
      }));

      // Le SAV est créé quoi qu'il arrive ; c'est la réponse au client qui peut
      // avoir échoué. On ne l'annonce pas comme un simple succès dans ce cas.
      const fd = res?.data?.result?.freshdesk;
      if (fd?.replyAttempted && !fd?.replySent) {
        await swal({
          title: "SAV créé, mais la réponse n'est pas partie",
          text: "Freshdesk a refusé la réponse. Le client a été prévenu par mail à la place (repli automatique), "
            + "mais le ticket ne porte pas votre message : répondez-lui à la main depuis Freshdesk.",
          icon: "warning",
        });
      } else if (fd?.ticketUpdated === false) {
        await swal({
          title: "SAV créé, ticket non mis à jour",
          text: "La réponse est bien partie au client, mais les champs du ticket Freshdesk n'ont pas pu être écrits. À compléter à la main.",
          icon: "warning",
        });
      } else {
        swal({ title: "SAV créé", icon: "success" });
      }
      onHide();
    } catch (e) {
      swal({
        title: "Échec de la création",
        text: e?.message || "Le SAV n'a pas été créé.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ticketLink = ticket?.ticketId
    ? `https://moulindev.freshdesk.com/a/tickets/${ticket.ticketId}`
    : null;

  /**
   * Le SAV est toujours créé sur la fiche ouverte, jamais sur le contact du ticket.
   * On alerte donc quand les deux ne se recoupent pas — sans bloquer : beaucoup de
   * clients écrivent au SAV depuis une adresse différente de celle de leur compte.
   */
  const identityWarning = (() => {
    if (!ticket || !customer?.id) return null;

    const ticketCustomer = ticket.customer;

    if (ticketCustomer && String(ticketCustomer.id) !== String(customer.id)) {
      // Les deux comptes sont décrits de la même façon — nom, email, id. Les
      // homonymes sont fréquents (plusieurs comptes pour une même personne) :
      // sans l'email et l'id des DEUX côtés, l'alerte a l'air de se contredire.
      const describe = (c) => `${c.firstname || ""} ${c.lastname || ""}`.trim()
        + ` — ${c.email || "email inconnu"}, compte #${c.id}`;

      return {
        level: "danger",
        otherCustomerId: ticketCustomer.id,
        // Deux formes du même avertissement : en lignes pour l'écran (un pavé
        // rouge de trois lignes ne se lit pas), en texte pour la confirmation.
        ticketPerson: describe(ticketCustomer),
        filePerson: describe(customer),
        text: `Ticket : ${describe(ticketCustomer)}. Fiche ouverte : ${describe(customer)} — c'est elle qui sera créditée. `
          + `Si c'est la même personne sous deux comptes, sachez que la cagnotte ira sur la fiche ouverte, pas sur celle du ticket.`,
      };
    }

    if (!ticketCustomer && ticket.contactEmail) {
      return {
        level: "warning",
        text: `Aucun compte client ne porte l'email du ticket (${ticket.contactEmail}). C'est fréquent — adresse pro d'un côté, perso de l'autre — mais vérifiez que la fiche ouverte est bien celle de cette personne avant de créditer.`,
      };
    }

    return null;
  })();

  // --- contenu de la fenêtre, découpé pour servir les deux modes -------------
  // Mode « formulaire » : tout est à l'écran, pour qui connaît l'outil.
  // Mode « pas à pas » : une question à la fois, pour qui fait un SAV de temps en temps.
  const ticketField = (cols, help = null) => (
    <Col {...cols}>
            <Form.Label className="mb-1"><b>Ticket Freshdesk</b></Form.Label>
            <div className="d-flex" style={{ gap: 8 }}>
              <Form.Control
                type="text"
                placeholder="Numéro du ticket, ex. 27292 — ou collez l'URL"
                value={ticketIdInput}
                onChange={(e) => setTicketIdInput(e.target.value)}
              />
              <Button variant="secondary" onClick={() => fetchTicket()} disabled={isFetchingTicket} style={{ whiteSpace: "nowrap" }}>
                <FontAwesomeIcon icon={faSearch} /> Récupérer
              </Button>
            </div>
            <Form.Text muted>
              {help || (
                <>
                  Laisser vide si le SAV ne vient pas d'un ticket. Le numéro se lit à la fin de
                  l'URL (…/a/tickets/<b>27292</b>) ; l'URL entière fonctionne aussi.
                </>
              )}
            </Form.Text>
          </Col>
  );

  const orderField = (cols) => (
    <Col {...cols}>
            <Form.Label className="mb-1">
              <b>Commande concernée</b>
              <small className="text-muted"> · {RECENT_ORDERS_LIMIT} dernières commandes</small>
            </Form.Label>
            <Form.Control
              as="select"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
            >
              <option value="">— Aucune commande rattachée</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.numberOrder}{o.date ? ` — ${o.date}` : ""}{o.price ? ` — ${(o.price / 100).toFixed(2)} €` : ""}
                </option>
              ))}
            </Form.Control>
            <Form.Text muted>
              {orderId === ""
                ? "Sans commande : établissement déduit de la dernière commande, et « % du montant » inutilisable."
                : "Elle sera reportée sur le ticket Freshdesk."}
            </Form.Text>
            {orderId !== "" && (
              <Button variant="link" size="sm" className="p-0" onClick={() => setShowOrderSheet(true)}>
                <FontAwesomeIcon icon={faFileAlt} /> Voir la commande
              </Button>
            )}
          </Col>
  );

  const ticketRecap = (
    <>
        {ticket && (
          <Alert variant="info">
            <div><b>{ticket.subject || "Ticket"}</b>{" "}
              {ticketLink && (
                <a href={ticketLink} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faExternalLinkAlt} /> ouvrir dans Freshdesk
                </a>
              )}
            </div>
            <div>
              Contact : {ticket.contactEmail || "inconnu"}
              {ticket.statusLabel && (
                <> · Statut : <b>{ticket.statusLabel}</b></>
              )}
            </div>
            {ticket.isDone && (
              <div className="text-warning">
                <FontAwesomeIcon icon={faLock} />{" "}
                <b>Ticket déjà {ticket.statusLabel.toLowerCase()}.</b>{" "}
                Envoyer une réponse le rouvrira. Vérifiez qu'un dédommagement n'a pas
                déjà été fait hors de l'outil.
              </div>
            )}
            <div>Commande : {ticket.order ? `#${ticket.order.numberOrder}` : "non renseignée sur le ticket"}</div>
            {ticket.existingSav && (
              <div className="text-danger">
                <b>Attention :</b> ce ticket porte déjà un SAV ({ticket.existingSav.label}).
              </div>
            )}
            {identityWarning && (
              <div className={identityWarning.level === "danger" ? "text-danger mt-2" : "text-warning mt-2"}>
                <FontAwesomeIcon icon={faUserSlash} />{" "}
                <b>{identityWarning.level === "danger" ? "Client différent." : "À vérifier :"}</b>{" "}
                {identityWarning.ticketPerson ? (
                  <>
                    La cagnotte ira sur la <b>fiche ouverte</b>, pas sur le contact du ticket.
                    <div className="mt-1"><span className="text-muted">Contact du ticket :</span> {identityWarning.ticketPerson}</div>
                    <div>
                      <span className="text-muted">Fiche ouverte, créditée :</span> {identityWarning.filePerson}
                      {identityWarning.otherCustomerId && (
                        <>
                          {" · "}
                          <a
                            href={buildSymfonyCustomersUrl(
                              `${process.env.REACT_APP_LEMOULIN_BO_URL}/e28XmZh3`,
                              identityWarning.otherCustomerId
                            )}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            ouvrir la fiche #{identityWarning.otherCustomerId}
                          </a>
                        </>
                      )}
                    </div>
                  </>
                ) : identityWarning.text}
              </div>
            )}
          </Alert>
        )}
    </>
  );

  const baremeBlock = (
    <>
        <BaremeSuggestion
          suggestion={suggestion}
          answers={baremeAnswers}
          onAnswerChange={(key, value) => setBaremeAnswers((prev) => ({ ...prev, [key]: value }))}
          onApply={applySuggestion}
          orderItems={orderItems}
          onShowBareme={() => setShowBareme(true)}
          onPickedChange={setConcernedItems}
        />
    </>
  );

  const variablesBlock = (sections) => (
    <Variables
    typeFilter={orderId ? "order" : "customer"}
    freeCreditAmount={form.freeCreditAmount}
    note={form.note}
    savCategory={form.savCategory}
    savCategories={propositions.savCategories}
    savCategoriesGrouped={propositions.savCategoriesGrouped}
    vigilanceLevel={form.vigilanceLevel}
    vigilanceLevels={propositions.vigilanceLevels}
    handleChange={handleFormChange}
    addCreditOrderAmount={form.addCreditOrderAmount}
    creditOrderAmountPercentage={form.creditOrderAmountPercentage}
    productOptions={form.productOptions}
    productCategories={propositions.categories}
    layout="modal"
    sections={sections}
    />
  );

  const currentTicketId = extractTicketId(ticketIdInput);

  // Un seul canal : le message part sur le ticket Freshdesk, qu'il préexiste ou
  // qu'on le crée à la fin. L'étape existe donc dans les deux cas — avant, sans
  // ticket, le client recevait un mail que personne n'avait relu.
  const reponseBlock = (
    <>
          <>
            <Form.Group>
              <Form.Label>
                <b>Message envoyé au client</b> — relisez-le, il part tel quel sur le ticket Freshdesk
                {currentTicketId === "" ? " créé pour ce SAV." : "."}
              </Form.Label>
              {/* Réponses types, comme les réponses enregistrées de Freshdesk :
                  filtrées par la catégorie du SAV, les plus ciblées d'abord. */}
              <Form.Control
                as="select"
                className="mb-2"
                value={templateId}
                onChange={(e) => {
                  const value = e.target.value;
                  setTemplateId(value);
                  if (value) generateResponseText(value);
                }}
              >
                <option value="">— Réponse type : écrire moi-même —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.scope === "all" ? " (toutes catégories)" : ""}
                  </option>
                ))}
              </Form.Control>
              <Form.Control
                type="text"
                className="mb-2"
                value={signature}
                placeholder="Signature : votre prénom (vide = l'équipe du Moulin)"
                onChange={(e) => {
                  setSignature(e.target.value);
                  try { localStorage.setItem("savSignature", e.target.value); } catch (err) { /* navigation privée */ }
                }}
              />
              {responseStale && (
                <Alert variant="warning" className="py-2 px-3 mb-2" style={{ fontSize: "0.88rem" }}>
                  <b>Le geste a changé depuis que ce message a été écrit.</b>{" "}
                  Vérifiez le montant annoncé au client, ou{" "}
                  <Button variant="link" className="p-0 align-baseline" onClick={() => generateResponseText()}>
                    régénérez le message
                  </Button>.
                </Alert>
              )}
              <Form.Control
                as="textarea"
                rows={8}
                value={responseText}
                placeholder="Laisser vide : le client reçoit alors le mail automatique du Moulin."
                onChange={(e) => {
                  setResponseText(e.target.value);
                  setResponseIsGenerated(false);
                }}
              />
              <Form.Text muted>
                {"{lien}"} est remplacé par le lien de choix du client. S'il y a un choix produit et
                que le marqueur a été supprimé, le lien est ajouté à la fin.
              </Form.Text>
              <Button variant="link" className="p-0 mt-1" onClick={() => generateResponseText()}>
                {templateId ? "Régénérer à partir de la réponse type" : "Générer un texte proposé"}
              </Button>
            </Form.Group>

            {/* Vaut aussi pour le ticket créé pour ce SAV : répondre le rouvre,
                c'est l'agent qui dit s'il doit rester ouvert ou non. Décoché tout
                seul quand le client a un produit à choisir. */}
            <Form.Check
              type="checkbox"
              className="mt-2"
              id="sav-close-ticket"
              label={currentTicketId !== ""
                ? "Clôturer le ticket Freshdesk après création"
                : "Clôturer le ticket créé pour ce SAV"}
              checked={closeTicket}
              onChange={(e) => { setCloseTicketTouched(true); setCloseTicket(e.target.checked); }}
            />
            <Form.Text muted>
              Fermé, une réponse du client n'atterrit pas dans la file de traitement.
              Laissez décoché si vous attendez quelque chose de lui.
            </Form.Text>
            {responseText.trim() === "" && (
              <p className="text-muted mt-2 mb-0" style={{ fontSize: "0.88rem" }}>
                Sans message, le client reçoit le mail automatique : son contenu reprend le motif
                saisi plus haut.
              </p>
            )}
          </>
    </>
  );

  const selectedOrder = orders.find((o) => String(o.id) === String(orderId));
  const categoryLabel = (propositions.savCategories || {})[form.savCategory] || form.savCategory;
  const offerLabels = (form.productOptions || [])
    .map((o) => ((propositions.categories || []).find((c) => c.key === o.category) || {}).label || o.category)
    .join(", ");

  const formBody = (
    <>
      <Step num={1} title="D'où vient ce SAV ?" hint="Ticket Freshdesk et commande concernée — les deux sont facultatifs" />
      <Row className="mb-2">
        {ticketField({ lg: 6 })}
        {orderField({ lg: 6 })}
      </Row>
      {ticketRecap}

      {/* La cause avant le geste : on ne choisit pas un dédommagement avant de
          savoir ce qui s'est passé, et c'est la catégorie qui déclenche le barème. */}
      <Step num={2} title="Que s'est-il passé ?" hint="Interne : catégorie pour les statistiques, motif pour le ticket et la trace" />
      {variablesBlock(["reason"])}

      <Step num={3} title="Ce qu'on accorde" hint="Le barème propose, vous décidez" />
      {baremeBlock}
      {variablesBlock(["cagnotte", "offer"])}

      <Step num={4} title="Ce que reçoit le client" hint="Part sur le ticket Freshdesk, tel quel — vide, c'est le mail automatique" />
      {reponseBlock}
    </>
  );

  // Une question par écran, dans l'ordre où on se les pose vraiment.
  const guidedSteps = [
    {
      key: "ticket",
      question: "Ce SAV vient-il d'un ticket Freshdesk ?",
      hint: "Répondez pour continuer : c'est ce qui décide si on répond sur le ticket du client ou si on lui envoie un mail.",
      content: (
        <>
          <div className="sav-yesno">
            <Button
              variant={fromTicket === true ? "success" : "outline-secondary"}
              onClick={() => setFromTicket(true)}
            >
              Oui, il y a un ticket
            </Button>
            <Button
              variant={fromTicket === false ? "success" : "outline-secondary"}
              onClick={() => {
                // Répondre « non » après avoir récupéré un ticket doit vraiment le
                // détacher, sinon la réponse partirait sur un ticket qu'on a dit ignorer.
                setFromTicket(false);
                setTicket(null);
                setTicketIdInput("");
              }}
            >
              Non, je le crée de zéro
            </Button>
          </div>
          {fromTicket === true && (
            <>
              <Row className="mt-4">
                {ticketField({ xs: 12 }, <>Le numéro se lit à la fin de l'URL (…/a/tickets/<b>27292</b>) ; l'URL entière fonctionne aussi.</>)}
              </Row>
              {ticketRecap}
            </>
          )}
          {fromTicket === false && (
            <p className="text-muted mb-0 mt-3">
              Un ticket Freshdesk sera créé à la fin, et le client sera prévenu par mail.
            </p>
          )}
        </>
      ),
    },
    {
      key: "order",
      question: "Sur quelle commande porte-t-il ?",
      hint: "Elle donne l'établissement, rend possible le « % du montant » et sert au barème.",
      content: <Row>{orderField({ xs: 12 })}</Row>,
    },
    {
      key: "motif",
      question: "Que s'est-il passé ?",
      hint: "Les deux sont internes : la catégorie sert aux statistiques, le motif au ticket et à la trace.",
      content: variablesBlock(["reason"]),
    },
    {
      key: "geste",
      question: "Quel geste accorde-t-on ?",
      hint: "Le barème propose d'après la catégorie et la commande ; vous gardez la main sur les montants.",
      content: (
        <>
          {baremeBlock}
          {variablesBlock(["cagnotte", "offer"])}
        </>
      ),
    },
    {
      key: "reponse",
      question: "Que reçoit le client ?",
      hint: "Le message part sur le ticket Freshdesk, tel quel. Laissé vide, c'est le mail automatique qui part.",
      content: reponseBlock,
    },
    {
      key: "recap",
      question: "On vérifie, puis on crée",
      hint: "Dernier regard avant de créditer le client.",
      content: (
        <ul className="list-unstyled mb-0" style={{ lineHeight: 1.9 }}>
          <li><b>Client :</b> {customer ? `${customer.firstname} ${customer.lastname}` : "—"}</li>
          <li><b>Ticket :</b> {currentTicketId !== "" ? `#${currentTicketId}` : "aucun — un ticket sera créé"}</li>
          <li><b>Commande :</b> {selectedOrder ? selectedOrder.numberOrder : "aucune"}</li>
          <li><b>Catégorie :</b> {form.savCategory ? categoryLabel : <span className="text-danger">à choisir</span>}</li>
          <li><b>Motif :</b> {form.note ? form.note : <span className="text-danger">à écrire</span>}</li>
          <li>
            <b>Geste :</b>{" "}
            {form.freeCreditAmount > 0 ? `${Number(form.freeCreditAmount).toFixed(2)} € de cagnotte` : null}
            {form.addCreditOrderAmount ? `${form.freeCreditAmount > 0 ? " + " : ""}${form.creditOrderAmountPercentage} % du montant de la commande` : null}
            {offerLabels ? `${form.freeCreditAmount > 0 || form.addCreditOrderAmount ? " + " : ""}au choix : ${offerLabels}` : null}
            {!(form.freeCreditAmount > 0) && !form.addCreditOrderAmount && !offerLabels && <span className="text-danger">aucun geste pour l'instant</span>}
          </li>
          <li>
            <b>Ce que reçoit le client :</b>{" "}
            {responseText.trim() !== ""
              ? (currentTicketId === "" ? "votre message, sur le ticket créé pour ce SAV" : "votre message, sur son ticket")
              : "le mail automatique du Moulin"}
          </li>
        </ul>
      ),
    },
  ];

  const currentStep = guidedSteps[Math.min(stepIndex, guidedSteps.length - 1)];
  const isLastStep = stepIndex >= guidedSteps.length - 1;
  // Seule la première étape bloque : on ne devine pas à la place de l'agent s'il
  // part d'un ticket. Les autres restent franchissables (le récapitulatif signale
  // ce qui manque avant la création).
  const canContinue = currentStep.key !== "ticket" || fromTicket !== null;

  const guidedBody = (
    <>
      <div className="sav-guided-head">
        <span className="sav-step-num">{Math.min(stepIndex + 1, guidedSteps.length)}</span>
        <div>
          <div className="sav-step-title">{currentStep.question}</div>
          {currentStep.hint && <div className="sav-step-hint ml-0">{currentStep.hint}</div>}
        </div>
      </div>
      {currentStep.content}
    </>
  );

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? () => {} : onHide}
      onEscapeKeyDown={isSubmitting ? () => {} : onEscapeKeyDown}
      backdrop={isSubmitting ? "static" : true}
      size="xl"
      dialogClassName="sav-modal-dialog"
      scrollable
    >
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>
          <FontAwesomeIcon icon={faLifeRing} />{" "}
          Faire un SAV {customer?.id ? `— ${customer.firstname} ${customer.lastname}` : ""}
        </Modal.Title>
        <ButtonGroup size="sm" className="ml-auto mr-3" aria-label="Mode de saisie">
          <Button variant={mode === "form" ? "success" : "outline-secondary"} onClick={() => changeMode("form")}>
            Formulaire
          </Button>
          <Button variant={mode === "guided" ? "success" : "outline-secondary"} onClick={() => changeMode("guided")}>
            Pas à pas
          </Button>
        </ButtonGroup>
        <Button variant="link" className="mr-3 align-self-center p-0 small" onClick={() => setShowBareme(true)}>
          <FontAwesomeIcon icon={faBalanceScale} /> Voir le barème
        </Button>
        <a href={SAV_NOTICE_URL} target="_blank" rel="noopener noreferrer" className="mr-3 align-self-center small">
          <FontAwesomeIcon icon={faBook} /> Notice SAV
        </a>
      </Modal.Header>
      <Modal.Body className="sav-modal-body">
        {/* La création enchaîne plusieurs appels distants (Freshdesk, Mailjet, SMS) :
            sans état d'attente explicite, l'agent croit que rien ne se passe et
            reclique — donc deux SAV. */}
        {isSubmitting ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="success" />
            <h5 className="mt-3">SAV en cours de création…</h5>
            <p className="text-muted mb-0">
              Crédit de la cagnotte
              {extractTicketId(ticketIdInput) !== "" && ", mise à jour du ticket Freshdesk"}
              {responseText.trim() !== "" && ", envoi de la réponse au client"}.
              <br />
              Merci de patienter, ne fermez pas la fenêtre.
            </p>
          </div>
        ) : (
        <>
        <SavCustomerHistory history={history} />

        {inProgress.length > 0 && (
          <Alert variant="warning">
            <b>
              <FontAwesomeIcon icon={faHourglassHalf} />{" "}
              {inProgress.length > 1 ? `${inProgress.length} propositions SAV déjà en cours` : "Une proposition SAV déjà en cours"} pour ce client
            </b>
            {" "}— vérifiez avant d'en créer une nouvelle : le client serait dédommagé deux fois.
            <ul className="mb-0 mt-2 pl-3">
              {inProgress.map((p) => (
                <li key={p.id} className="mb-2">
                  <b>{p.statusLabel}</b> · créée le {formatDateTime(p.createdAt)} ({p.source === "bo" ? "BO" : "Freshdesk"})
                  {p.cagnotteComputedAmount > 0 && <> · cagnotte {(p.cagnotteComputedAmount / 100).toFixed(2)} € déjà créditée</>}
                  {(p.productOptionsLabeled || []).length > 0 && <> · au choix : {p.productOptionsLabeled.map((o) => o.label).join(", ")}</>}
                  {p.expiresAt && p.status !== "recontact_requested" && <> · appliquée d'office le {formatDateTime(p.expiresAt)}</>}
                  {p.reason && <><br /><small className="text-muted">Motif : {p.reason}</small></>}
                  <br />
                  <small>
                    {p.freshdeskTicketLink && (
                      <><a href={p.freshdeskTicketLink} target="_blank" rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> ticket #{p.freshdeskTicketId}</a> · </>
                    )}
                    {p.publicLink && (
                      <><a href={p.publicLink} target="_blank" rel="noopener noreferrer">page de choix du client</a> · </>
                    )}
                    <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => cancelInProgress(p)}>Annuler</Button>
                  </small>
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {mode === "guided" ? guidedBody : formBody}
        </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {mode === "guided" && !isSubmitting ? (
          <>
            <small className="text-muted mr-auto">Étape {Math.min(stepIndex + 1, guidedSteps.length)} sur {guidedSteps.length}</small>
            <Button variant="light" onClick={onHide}>Annuler</Button>
            <Button variant="light" onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={stepIndex === 0}>
              Retour
            </Button>
            {isLastStep ? (
              <Button variant="success" onClick={submit}>Créer le SAV</Button>
            ) : (
              <Button variant="success" onClick={() => setStepIndex((i) => i + 1)} disabled={!canContinue}>
                Continuer
              </Button>
            )}
          </>
        ) : (
          <>
            <Button variant="light" onClick={onHide} disabled={isSubmitting}>Annuler</Button>
            <Button variant="success" onClick={submit} disabled={isSubmitting}>
              {isSubmitting ? "Création en cours…" : "Créer le SAV"}
            </Button>
          </>
        )}
      </Modal.Footer>

      <BaremeModal show={showBareme} onHide={() => setShowBareme(false)} />

      <OrderSheetModal
        show={showOrderSheet}
        onHide={() => setShowOrderSheet(false)}
        orderId={orderId}
        order={orderSummary}
      />
    </Modal>
  );
};

export default PopupSav;
